import { BordBadge, BordButton, BordOneToneIcon, BordPhoto, TbordOneToneIcon } from 'bord-design-system';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IQuoteHistory, IQuoteStatus } from 'types/quoteDetails.types';
import QuoteStatusRow from 'views/Quote/components/QuoteStatusRow/QuoteStatusRow';
import { EQuoteType, IQuoteRequestedByUser, IQuoteRequestedCountry, IQuoteResponsibleUser } from 'types/quote.types';
import QuoteDetailHeaderSkeleton from './components/QuoteDetailHeaderSkeleton/QuoteDetailHeaderSkeleton';
import AddResponsiblesButton from '../AddResponsiblesButton/AddResponsiblesButton';
import QuoteDetailHistoryModal from '../QuoteDetailHistoryModal/QuoteDetailHistoryModal';
import { formatShortDate, formatShortDateAndHour } from 'utils/formatDatesAndHours';

import './QuoteDetailHeader.scss';

interface IClientInfoItem {
  id: number;
  nameList: string;
  valueList?: string;
  width: number;
  firstIcon?: TbordOneToneIcon;
  secondIcon?: TbordOneToneIcon;
  stroke?: string;
  url?: string;
}

interface IQuoteDetailHeader {
  isLoading?: boolean;
  quoteStatus?: IQuoteStatus;
  history?: IQuoteHistory;
  responsibles?: IQuoteResponsibleUser[];
  onResponsiblesUpdate?: () => void;
  id?: number;
  requestedByUser?: IQuoteRequestedByUser;
  requestedCountry?: IQuoteRequestedCountry;
  createdAt?: string;
  organizationName?: string;
  organizationId?: number;
  parentQuoteId?: number;
  lastItemStatusChange?: string;
  onCancelSuccess?: () => Promise<void>;
  referenceCode?: string;
  quoteType?: EQuoteType;
}

/**
 * Encabezado de detalle de cotización con información general, última actualización y modal de historial.
 */
const QuoteDetailHeader = ({
  isLoading,
  quoteStatus,
  history,
  responsibles,
  onResponsiblesUpdate,
  id,
  requestedByUser,
  requestedCountry,
  createdAt,
  organizationName,
  organizationId,
  parentQuoteId,
  lastItemStatusChange,
  onCancelSuccess,
  referenceCode,
  quoteType
}: IQuoteDetailHeader) => {
  const { push } = useHistory();
  const [clientInfoList, setClientInfoList] = useState<IClientInfoItem[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const isExploratory = quoteType === EQuoteType.EXPLORATORY;
  const creationDateLabel = formatShortDate(createdAt)?.onlyDate;

  const buildClientInfoList = () => {
    setClientInfoList([
      {
        id: 1,
        nameList: 'Cliente ',
        valueList: organizationName,
        width: 145,
        ...(isExploratory
          ? {}
          : {
              secondIcon: 'externalLink' as TbordOneToneIcon,
              stroke: '#00B188',
              url: `/organization/details/${organizationId}`
            })
      },
      {
        id: 2,
        nameList: 'Solicitante',
        valueList: `${requestedByUser?.firstName} ${requestedByUser?.lastName}`,
        width: 200,
        firstIcon: 'bord',
        stroke: '#070F21'
      },
      {
        id: 3,
        nameList: 'Número de contacto',
        valueList: `+ ${requestedByUser?.phoneCode || ''} ${requestedByUser?.phone || ''}`,
        width: 200
      },
      { id: 4, nameList: 'Correo', valueList: `${requestedByUser?.email}`, width: 200 }
    ]);
  };

  const showBordPhoto = () => {
    if (!requestedByUser?.photoProfile)
      return (
        <div className="bordPhotoPlaceholder">
          <BordOneToneIcon variant="user" standardSize={32} stroke="#B4B4B4" />
        </div>
      );
    return (
      <BordPhoto
        size="s56"
        url={requestedByUser?.photoProfile || ''}
        imageProps={{
          style: {
            borderRadius: '0.672rem',
            objectFit: 'cover',
            height: '100%',
            width: '100%',
            objectPosition: 'center center'
          }
        }}
        style={{
          border: '0.56px solid #B4B4B4',
          borderRadius: '0.672rem'
        }}
      />
    );
  };

  useEffect(() => {
    buildClientInfoList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedByUser, isExploratory, organizationName, organizationId]);

  if (isLoading) return <QuoteDetailHeaderSkeleton />;
  return (
    <div className="quoteDetailHeader">
      <QuoteDetailHistoryModal
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        quoteStatus={quoteStatus}
        history={history}
      />
      <div className="topRow">
        <div className="topLeft">
          <div className="quoteNumber">
            <div className="titleRow">Cotización N°{id}</div>
            <div className="metaRow">
              <QuoteStatusRow
                quoteId={id}
                quoteStatus={quoteStatus}
                parentQuoteId={parentQuoteId}
                onCancelSuccess={onCancelSuccess}
              />
            </div>
          </div>
          {isExploratory && (
            <div className="exploratoryMetaRow">
              <BordBadge label="Exploratoria" variant="gradient" size="small" />
              <span className="exploratoryCreationDate">
                <span className="exploratoryCreationDateLabel">Creación:</span> {creationDateLabel}
              </span>
            </div>
          )}
          {!isExploratory && (
            <div className="countryAndCreationDate">
              <div className="containerCountryAndDate">
                <div className="titleContainer">País:</div>
                <div className="subtitleContainer">{requestedCountry?.name}</div>
                {requestedCountry?.flag && (
                  <div className="countryFlag">
                    <img alt={`Bandera de ${requestedCountry?.name}`} src={requestedCountry?.flag} />
                  </div>
                )}
              </div>
              <div className="containerCountryAndDate">
                <div className="titleContainer">Creación:</div>
                <div className="subtitleContainer">{creationDateLabel}</div>
              </div>
              {referenceCode && (
                <div className="containerReferenceCode">
                  <div className="titleContainer">Referencia del cliente:</div>
                  <div className="subtitleContainer">{referenceCode}</div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="lastUpdate">
          <div className="lastUpdateLeft">
            <div className="lastUpdateIconWrapper">
              <BordOneToneIcon variant="time5" standardSize={18} stroke="#4096F0" />
            </div>
            <div className="lastUpdateDetails">
              <div className="lastUpdateLabel">ÚLTIMA ACTUALIZACIÓN</div>
              <div className="lastUpdateDescription">Descripción</div>
              <div className="lastUpdateDate">
                <span className="lastUpdateDot" />
                {formatShortDateAndHour(lastItemStatusChange)?.onlyDateAndHour ?? '-'}
              </div>
            </div>
          </div>
          <div className="lastUpdateRight">
            <BordButton
              label="Ver historial"
              bordButtonStyle="outlined"
              customClassName="viewHistoryBtn"
              onClick={() => setIsHistoryOpen(true)}
              customFontSize="1.2rem"
              disabled
            />
          </div>
        </div>
      </div>
      <div className="divider" />
      <div className="downRow">
        <div className="downRowLeft">
          {showBordPhoto()}
          <div className="clientInfoListContainer">
            {clientInfoList?.map(item => (
              <div key={item.id} className="clientInfoItem" style={{ width: `${item.width / 10}rem` }}>
                <div className="clientInfoItemName">{item.nameList}</div>
                <div
                  className={`clientInfoItemValue${item.url ? ' cursor-pointer' : ''}`}
                  onClick={() => item.url && push(item.url)}
                >
                  {item.firstIcon && (
                    <BordOneToneIcon variant={item.firstIcon} standardSize={14} stroke={item.stroke} />
                  )}
                  {item.valueList}
                  {item.secondIcon && (
                    <BordOneToneIcon variant={item.secondIcon} standardSize={14} stroke={item.stroke} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="downRowRight">
          <AddResponsiblesButton quoteId={id} responsibles={responsibles} onResponsiblesUpdate={onResponsiblesUpdate} />
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailHeader;
